import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { listAppDatasetVersionsTE, PER_PAGE } from 'api/datasets/listDatasetVersions';
import { pipe } from 'fp-ts/lib/function';
import { queryKeys, useInfiniteQueryTE, useQueryTE } from 'utils/react-query';
import { errorToReactLeft } from 'utils/fp';
import { either } from 'fp-ts';
import { StatusCode } from 'api/grpc/resources';

export function useListDatasetVersions() {
  const router = useRouter();
  const { datasetId, userOrOrgId, appId } = router.query as Record<string, string>;

  return useInfiniteQueryTE(
    [queryKeys.DataSetVersions, { appId, userId: userOrOrgId, datasetId }],
    ({ pageParam = 1 }) => {
      return listAppDatasetVersionsTE(
        {
          appId,
          userId: userOrOrgId,
          datasetId,
          page: pageParam,
          perPage: PER_PAGE,
        },
        errorToReactLeft,
      );
    },
    {
      enabled: Boolean(appId) && Boolean(userOrOrgId) && Boolean(datasetId),
      retry: false,
      getNextPageParam: (lastPage_, allPages) =>
        pipe(
          lastPage_,
          either.fold(
            () => undefined,
            (lastPage): number | undefined => {
              return lastPage.dataset_versions.length < PER_PAGE ? undefined : allPages.length + 1;
            },
          ),
        ),
    },
  );
}

type UseDatasetVersionsListProps = {
  appId: string;
  userId: string;
  datasetId: string;
  notFailed?: boolean;
};
/**
 * Hook to fetch and manage dataset versions.
 *
 * @param {Object} props - The properties for fetching dataset versions.
 * @param {string} props.appId - The application ID.
 * @param {string} props.userId - The user ID.
 * @param {string} props.datasetId - The dataset ID.
 * @param {boolean} props.notFailed - Whether to filter for dataset versions that have not failed.
 * @returns {Object} - An object containing isLoading and data.
 * @returns {boolean} isLoading - Indicates if the data is currently being fetched.
 * @returns {Array} data - The fetched dataset versions or an empty array if loading or error.
 */
export function useDatasetVersionsList({ appId, userId, datasetId, notFailed }: UseDatasetVersionsListProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<CF.API.Datasets.DatasetVersion[]>([]);

  const { data: queryData, isFetching } = useQueryTE(
    [queryKeys.DataSetVersions, { appId, userId, datasetId }],
    listAppDatasetVersionsTE({ appId, userId, datasetId, page: 1, perPage: 100 }, errorToReactLeft),
  );

  useEffect(() => {
    if (isFetching) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
      pipe(
        queryData,
        either.fold(
          () => {},
          (response) => {
            const fetchedDatasetVersions = response.dataset_versions || [];
            const datasetVersions = notFailed
              ? fetchedDatasetVersions.filter(
                  (datasetVersion) =>
                    datasetVersion.status.code !== StatusCode.DATASET_VERSION_FAILURE &&
                    datasetVersion.status.code !== StatusCode.DATASET_VERSION_UNEXPECTED_ERROR,
                )
              : fetchedDatasetVersions;

            setData(datasetVersions);
          },
        ),
      );
    }
  }, [isFetching, queryData]);

  return { isLoading, data };
}
